import { cleanupRenderNode, DATA_HIDE_NODE, DATA_RENDER_NODE, handleRenderNode, RenderNode } from "./utility";

const selectors = [DATA_HIDE_NODE, DATA_RENDER_NODE];
const selectorStr = selectors.join(',');
const SHADOW_HOST_SELECTOR = '[shadow]';

const isRenderNode = (node: HTMLElement): node is RenderNode => selectors.some((s) => node.matches(s));

const forEachRenderNode = (root: HTMLElement | ShadowRoot, fn: (node: RenderNode) => void) => {
  if (root instanceof HTMLElement && isRenderNode(root)) {
    fn(root);
  }

  root
    .querySelectorAll<RenderNode>(selectorStr)
    .forEach(fn);
};

const forEachShadowRoot = (root: HTMLElement | ShadowRoot, fn: (shadowRoot: ShadowRoot) => void) => {
  if (root instanceof HTMLElement && root.hasAttribute('shadow') && root.shadowRoot) {
    fn(root.shadowRoot);
  }

  root
    .querySelectorAll<HTMLElement>(SHADOW_HOST_SELECTOR)
    .forEach((host) => host.shadowRoot && fn(host.shadowRoot));
};

const registerRenderNodes = (root: HTMLElement | ShadowRoot) => {
  const observed = new WeakSet<Node>();
  const observers = new WeakMap<Node, MutationObserver>();
  const activeObservers = new Set<MutationObserver>();

  const disconnect = (node: Node) => {
    const observer = observers.get(node);
    if (!observer) return;
    observer.disconnect();
    observers.delete(node);
    activeObservers.delete(observer);
  };

  const cleanupShadowTree = (shadowRoot: ShadowRoot) => {
    forEachRenderNode(shadowRoot, cleanupRenderNode);
    forEachShadowRoot(shadowRoot, cleanupShadowTree);
    disconnect(shadowRoot);
  };

  const handleAdded = (node: HTMLElement) => {
    forEachRenderNode(node, handleRenderNode);
    forEachShadowRoot(node, watch);
  };

  const handleRemoved = (node: HTMLElement) => {
    forEachRenderNode(node, cleanupRenderNode);
    forEachShadowRoot(node, cleanupShadowTree);
  };

  const watch = (node: HTMLElement | ShadowRoot) => {
    if (observed.has(node)) return;
    observed.add(node);

    forEachRenderNode(node, handleRenderNode);
    forEachShadowRoot(node, watch);

    const mo = new MutationObserver(muts => {
      for (const m of muts) {
        for (const added of Array.from(m.addedNodes)) {
          if (!(added instanceof HTMLElement)) continue;
          handleAdded(added);
        }
        for (const removed of Array.from(m.removedNodes)) {
          if (!(removed instanceof HTMLElement)) continue;
          handleRemoved(removed);
        }
      }
    });

    mo.observe(node, {
      childList: true,
      subtree  : true
    });
    observers.set(node, mo);
    activeObservers.add(mo);
  };

  watch(root);
  return () => {
    for (const observer of activeObservers) {
      observer.disconnect();
    }
    activeObservers.clear();
  };
}

export { registerRenderNodes };
